import * as vp from 'vopay';
import { FunctionComponent, JSX } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '../../../../providers/NavMenu.provider';

const { Table, Box, SubTitle, Badge, Button, formattedCurrency } = vp;

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  clientAccountId: string;
};

type TransactionInfoProps = {
  transaction: vp.EftFundingTransactionResponse;
  Notes: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  bankAccountDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const EftFundingTransactionInfo: FC = (props: TransactionInfoProps) => {
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();
  const { transaction, Notes } = props;
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.EftFundingTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetail')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('accountHolderDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('accountHolderDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('bankAccountDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('bankAccountDetails')} />
      {transaction.FailureReason && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={vp.capitalizeFirstLetter(
                  tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                    defaultValue: transaction.TransactionStatus,
                  }),
                )}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDetails.transactionDate'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('transactionDetails.holdReleaseDate'),
            value: transaction.HoldReleaseDateTime,
          },
          {
            display: t('transactionDetails.type'),
            value: t('fund') || '',
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
          },
          {
            display: t('transactionDetails.notes'),
            value: Notes,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetailsProps;
      },
      accountHolderDetails: () => {
        const accountHolderDetailsPairs: KeyValuePair[] = [
          {
            display: t('accountHolderDetails.name'),
            value: (
              <Box jc="fs" ai="center" gap="sm">
                <Box>{`${transaction.FirstName} ${transaction.LastName}`}</Box>
                {state && state.clientAccountId && state.clientAccountId !== '' && (
                  <Button
                    id="view-client-account-details"
                    label={t('accountHolderDetails.view')}
                    width="50px"
                    height="30px"
                    color="secondary"
                    onClick={() => {
                      history.push(`/client-accounts/${state.clientAccountId}`);
                      onSubNavClick(
                        'clientAccountsList',
                        `/client-accounts/${state.clientAccountId}`,
                        t('accountHolderDetails.clientAccountsList'),
                      );
                    }}
                  />
                )}
              </Box>
            ),
            visible: !!transaction.FirstName,
          },
          {
            display: t('accountHolderDetails.companyName'),
            value: transaction.CompanyName,
            visible: !!transaction.CompanyName,
          },
          {
            display: t('accountHolderDetails.address'),
            value: `${transaction.Address1} ${transaction.Address2}`,
            visible: transaction.Address1 !== '',
          },
          {
            display: t('accountHolderDetails.city'),
            value: transaction.City,
            visible: transaction.City !== '',
          },
          {
            display: t('accountHolderDetails.postalCode'),
            value: transaction.PostalCode,
            visible: !!transaction.PostalCode,
          },
          {
            display: t('accountHolderDetails.country'),
            value: transaction.Country,
            visible: true,
          },
          {
            display: t('accountHolderDetails.primaryContact'),
            value: transaction.PhoneNumber,
            visible: transaction.PhoneNumber !== '',
          },
        ];

        const accountHolderDetailsProps: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('accountHolderDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountHolderDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetailsProps;
      },
      bankAccountDetails: () => {
        const bankingInformationPairs: KeyValuePair[] = [
          {
            display: t('bankAccountDetails.financialInstitutionNumber'),
            value: transaction.FinancialInstitutionNumber,
          },
          {
            display: t('bankAccountDetails.branchTransitNumber'),
            value: transaction.BranchTransitNumber,
          },
          {
            display: t('bankAccountDetails.accountNumber'),
            value: transaction.AccountNumber,
          },
        ];

        const bankingInformationProps: vp.TableProps = {
          name: 'banking-information',
          variant: 'pair',
          columns: {
            detailKey: { header: t('bankAccountDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: bankingInformationPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return bankingInformationProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: transaction.FailureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default EftFundingTransactionInfo;
